import type { JSX } from 'react'

import { describeExpiry, describeMethod } from '../lib/format.ts'
import type { PaymentMethodSummary } from '../lib/types.ts'

export interface MethodListProps {
  methods: PaymentMethodSummary[]
  canCreate: boolean
  onSelect: (paymentMethodId: number) => void
  onAdd: () => void
}

/**
 * Lists the customer's saved payment methods for selection.
 * @param props - The methods and the select and add callbacks.
 * @returns The list.
 */
export function MethodList(props: MethodListProps): JSX.Element {
  const { methods, canCreate, onSelect, onAdd } = props
  return (
    <section
      data-testid="method-list"
      aria-labelledby="method-list-heading"
      className="flex flex-col gap-3"
    >
      <h2 id="method-list-heading" className="blytz-drawer__title">
        {'Payment methods'}
      </h2>
      {methods.length === 0 ? (
        <div data-testid="method-list-empty" className="blytz-empty">
          <p className="blytz-empty__title">{'No saved cards'}</p>
          <p className="blytz-empty__desc">
            {'This customer has no saved payment methods.'}
          </p>
        </div>
      ) : (
        <ul data-testid="method-options" className="flex flex-col gap-3">
          {methods.map(method => (
            <li key={method.paymentMethodId}>
              <button
                type="button"
                data-testid={`method-${method.paymentMethodId}`}
                className="blytz-card blytz-card--selectable w-full text-left"
                onClick={() => onSelect(method.paymentMethodId)}
              >
                <span className="blytz-pm__label block">
                  {describeMethod(method)}
                </span>
                <span className="blytz-pm__meta block">
                  {describeExpiry(method.expMonth, method.expYear)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {canCreate && (
        <button
          type="button"
          data-testid="method-add-card"
          className="blytz-btn blytz-btn--primary blytz-btn--full"
          onClick={onAdd}
        >
          {'Add a card'}
        </button>
      )}
    </section>
  )
}
